import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, from, map, switchMap } from 'rxjs';
import { environment } from '@environment/environment';

/** Contenuto del Blob in base64 (senza il prefisso "data:...;base64,"), come lo vuole il backend. */
function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',', 2)[1] ?? '');
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

export interface RagChatRequest {
  session_id: string;
  message: string;
  language: string;
  /** Documento su cui e' la conversazione (uuid); assente = tutti i documenti pronti. Vale alla creazione. */
  scope?: string | null;
  attachments?: File[];
}

/** Fonte citata nella risposta: index e' il numero che compare nel testo, es. [1]. */
export interface RagChatReference {
  index?: number;
  source: string;
  pages?: number[];
  /** Fonti salvate prima delle citazioni numerate: una sola pagina */
  page?: number | null;
  /** File dell'azienda da aprire; null se non collegato */
  file_uuid?: string | null;
  /** Falso se il documento e' stato eliminato o escluso dall'AI */
  available?: boolean;
}

export interface RagChatResponse {
  success: boolean;
  data: {
    session_id: string;
    answer: string;
    references?: RagChatReference[];
  };
}

/** Conversazione nella cronologia dell'utente. */
export interface RagSessionSummary {
  session_id: string;
  title: string;
  updated_at: string;
  message_count: number;
  scope: string | null;
  scope_name: string | null;
}

export interface RagSessionMessage {
  user_message: string;
  user_message_at: string;
  ai_answer: string;
  ai_answer_at: string | null;
  references: RagChatReference[];
}

export interface RagSessionDetail {
  session_id: string;
  title: string;
  scope: string | null;
  scope_name: string | null;
  messages: RagSessionMessage[];
}

@Injectable({
  providedIn: 'root'
})
export class RagApiService {
  private http = inject(HttpClient);
  private readonly base = `${environment.api.base_url}/rag`;

  chat(request: RagChatRequest): Observable<RagChatResponse> {
    const formData = new FormData();
    formData.append('session_id', request.session_id);
    formData.append('message', request.message);
    formData.append('language', request.language);
    if (request.scope) {
      formData.append('scope', request.scope);
    }
    request.attachments?.forEach(file => formData.append('attachments', file));
    return this.http.post<RagChatResponse>(`${this.base}/chat`, formData);
  }

  /** Trascrive una domanda registrata a voce (OVH Whisper, con riserva sul Whisper locale). */
  transcribe(audio: Blob, filename: string, language: string): Observable<string> {
    return from(blobToBase64(audio)).pipe(
      // Il backend avvolge le risposte in {success, data: {...}} (CustomJSONRenderer)
      switchMap((content) => this.http.post<{data?: {text?: string}}>(`${this.base}/speech/transcribe`,
        {content, filename, language})),
      map((r) => (r.data?.text ?? '').trim()),
    );
  }

  sessions(): Observable<RagSessionSummary[]> {
    return this.http.get<{data: RagSessionSummary[]}>(`${this.base}/sessions`).pipe(map((r) => r.data ?? []));
  }

  session(sessionId: string): Observable<RagSessionDetail> {
    return this.http.get<{data: RagSessionDetail}>(`${this.base}/sessions/${encodeURIComponent(sessionId)}`)
      .pipe(map((r) => r.data));
  }

  deleteSession(sessionId: string): Observable<unknown> {
    return this.http.delete(`${this.base}/sessions/${encodeURIComponent(sessionId)}`);
  }
}
